import { TipoCompensacaoManual } from '@acme/core'
import { Chip } from '@/global/components/Chip'
import { DatePickerMultiple } from '@/global/components/DatePickerMultiple'
import { DatePickerSingle } from '@/global/components/DatePickerSingle'
import { SelectEmpresa, SelectFuncionario } from '@/global/components/Selects'
import Grid from '@/global/components/ui/Grid'
import { Label } from '@/global/components/ui/label'
import { cn } from '@/lib/utils'
import { Alert, Card, CardContent } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

export default function FormCompensacaoManual() {
    const { control, watch, getValues, setValue } = useFormContext()

    const tipo = watch('tipo')
    const data = watch('data')
    const destinos = watch('destinos') || []
    const empresaId = getValues('empresaId')
    const funcionarioId = getValues('funcionarioId')

    const isEditMode = Boolean(empresaId && funcionarioId)

    const getAlertMessage = () => {
        if (!tipo) {
            return 'Selecione o tipo de compensação para continuar'
        }

        const dataFormatada = data
            ? dayjs(data).format('DD/MM/YYYY (dddd)')
            : ''

        if (tipo === TipoCompensacaoManual.COMPENSAR) {
            if (destinos.length === 0) {
                return `O dia ${dataFormatada} possui horas extras. Você pode adicionar os dias com faltas que deseja compensar.`
            }
            return `O dia ${dataFormatada} possui horas extras que vão compensar as faltas de ${destinos.length} dia(s) selecionado(s).`
        }

        if (tipo === TipoCompensacaoManual.COMPENSADO) {
            if (destinos.length === 0) {
                return `O dia ${dataFormatada} possui faltas. Você pode adicionar os dias com horas extras para compensar.`
            }
            return `As faltas do dia ${dataFormatada} serão compensadas pelas horas extras de ${destinos.length} dia(s) selecionado(s).`
        }

        if (tipo === TipoCompensacaoManual.MESMO_DIA) {
            return `O dia ${dataFormatada} possui horas extras e faltas que serão compensadas entre si.`
        }

        return null
    }

    useEffect(() => {
        if (tipo === TipoCompensacaoManual.MESMO_DIA) {
            setValue('destinos', [])
        }
    }, [tipo])

    const getDataLabel = () => {
        if (tipo === TipoCompensacaoManual.COMPENSAR)
            return 'Dia com horas extras'
        if (tipo === TipoCompensacaoManual.COMPENSADO) return 'Dia com faltas'
        if (tipo === TipoCompensacaoManual.MESMO_DIA)
            return 'Dia com horas extras e faltas'
        return 'Data'
    }

    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <SelectEmpresa
                    width="100%"
                    size="medium"
                    disabled={isEditMode}
                />
            </Grid>
            <Grid item xs={12}>
                <SelectFuncionario
                    width="100%"
                    size="medium"
                    disabled={isEditMode}
                />
            </Grid>

            <Grid item xs={12}>
                <Label className="block mb-2">Tipo de compensação</Label>
                <Controller
                    name="tipo"
                    control={control}
                    render={({ field }) => (
                        <div className={cn('flex gap-4')}>
                            <CardTipo
                                cardValue={{
                                    value: TipoCompensacaoManual.COMPENSAR,
                                    label: 'Compensar',
                                    description: 'Dia com horas extras',
                                }}
                                field={field}
                            />

                            <CardTipo
                                cardValue={{
                                    value: TipoCompensacaoManual.COMPENSADO,
                                    label: 'Compensado',
                                    description: 'Dia com faltas',
                                }}
                                field={field}
                            />

                            <CardTipo
                                cardValue={{
                                    value: TipoCompensacaoManual.MESMO_DIA,
                                    label: 'Mesmo dia',
                                    description: 'Compensação diária manual',
                                }}
                                field={field}
                            />
                        </div>
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name="data"
                    control={control}
                    render={({ field }) => (
                        <DatePickerSingle
                            {...field}
                            label={getDataLabel()}
                            disabled={!tipo}
                        />
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name="destinos"
                    control={control}
                    render={({ field }) => (
                        <DatePickerMultiple
                            {...field}
                            label={
                                tipo === TipoCompensacaoManual.COMPENSAR
                                    ? 'Dias com faltas a compensar'
                                    : tipo === TipoCompensacaoManual.COMPENSADO
                                      ? 'Dias com horas extras'
                                      : 'Dias relacionados'
                            }
                            disabled={
                                !tipo ||
                                tipo === TipoCompensacaoManual.MESMO_DIA
                            }
                        />
                    )}
                />
            </Grid>

            {destinos.length > 0 && (
                <Grid item xs={12}>
                    <div className="flex gap-2 flex-wrap">
                        {destinos
                            .sort(
                                (a: { data: Date }, b: { data: Date }) =>
                                    new Date(a.data).getTime() -
                                    new Date(b.data).getTime(),
                            )
                            .map((item: { data: Date }, index: number) => (
                                <Chip
                                    key={index}
                                    label={dayjs
                                        .utc(item.data)
                                        .format('DD/MM/YYYY')}
                                    onDelete={() => {
                                        const newDestinos = destinos.filter(
                                            (_: any, i: number) => i !== index,
                                        )
                                        setValue('destinos', newDestinos)
                                    }}
                                />
                            ))}
                    </div>
                </Grid>
            )}

            <Grid item xs={12}>
                <Alert severity={!tipo ? 'warning' : 'info'}>
                    {getAlertMessage()}
                </Alert>
            </Grid>
        </Grid>
    )
}

interface CardTipoProps {
    cardValue: {
        value: string
        label: string
        description: string
    }
    field: {
        value: string
        onChange: (value: string) => void
    }
}
function CardTipo({ field, cardValue }: CardTipoProps) {
    return (
        <Card
            className={cn(
                'flex-1 cursor-pointer transition-colors duration-200 hover:bg-primary/10',
                {
                    'ring-2 ring-primary shadow-lg':
                        field.value === cardValue.value,
                },
            )}
            onClick={() => field.onChange(cardValue.value)}
        >
            <CardContent>
                <div className="flex items-center gap-3">
                    <div
                        className={cn(
                            'size-5 rounded-full border-2 flex items-center justify-center',
                            {
                                'border-primary':
                                    field.value === cardValue.value,
                                'border-muted-foreground':
                                    field.value !== cardValue.value,
                            },
                        )}
                    >
                        {field.value === cardValue.value && (
                            <div className="size-3 rounded-full bg-primary" />
                        )}
                    </div>
                    <p className="text-base font-semibold">{cardValue.label}</p>
                </div>
                <p className="text-sm text-muted-foreground mt-2 ml-9">
                    {cardValue.description}
                </p>
            </CardContent>
        </Card>
    )
}
